import { SITE_LOGO } from "@/lib/constants";
import { createClient } from "@/lib/supabase/client";
import type { EnrichedProfile, Profile } from "../supabase/types";
import { isValidUUID } from "../utils";

interface ProfileAvatarResponse {
	success: boolean;
	error: Error | null;
	avatarUrl: string;
}

export async function getProfileAvatar(
	userId: string,
): Promise<ProfileAvatarResponse> {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.select("avatar_url")
		.eq("id", userId)
		.single();
	if (error) {
		return { success: false, error, avatarUrl: SITE_LOGO };
	}
	return {
		success: true,
		error: null,
		avatarUrl: data?.avatar_url ?? SITE_LOGO,
	};
}

export async function deleteProfileAvatar(userId: string) {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.update({ avatar_url: null })
		.eq("id", userId)
		.single();
	return { success: true, error };
}

export async function deleteProfileCoverImage(userId: string) {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.update({ cover_image_url: null })
		.eq("id", userId)
		.single();
	return { success: true, error };
}

export async function getProfile(
	userId: string,
): Promise<{ data: EnrichedProfile | null; error: Error | null }> {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.select(
			"*, city:cities(*), user_type:user_types(*), account_status:account_status_types(*)",
		)
		.eq("id", userId)
		.single()
		.overrideTypes<EnrichedProfile>();
	return { data: data as EnrichedProfile, error };
}

export async function getProfileByUsername(
	username: string,
): Promise<{ data: EnrichedProfile | null; error: Error | null }> {
	if (isValidUUID(username)) {
		console.debug("Fetching profile by ID...", username);
		return getProfile(username);
	}

	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.select(
			"*, city:cities(*), user_type:user_types(*), account_status:account_status_types(*)",
		)
		.eq("username", username)
		.single()
		.overrideTypes<EnrichedProfile>();
	return { data: data as EnrichedProfile, error };
}

export async function getProfileUsername(
	id: string,
): Promise<{ data: string | null; error: Error | null }> {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.select("username")
		.eq("id", id)
		.single();
	return { data: data?.username || null, error };
}

export async function updateProfile(
	userId: string,
	data: Partial<
		Omit<Profile, "id" | "created_at" | "deleted_at" | "updated_at">
	>,
) {
	const supabase = createClient();
	const { data: profile, error } = await supabase
		.from("profiles")
		.update(data)
		.eq("id", userId)
		.single()
		.overrideTypes<EnrichedProfile>();
	return { profile: data as EnrichedProfile, error };
}

export async function deleteProfile(userId: string) {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.delete()
		.eq("id", userId)
		.single();
	return { data, error };
}

export async function searchProfile(q: string) {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("profiles")
		.select(
			"*, city:cities(*), user_type:user_types(*), account_status:account_status_types(*)",
		)
		.ilike("username", `%${q}%`)
		.limit(10)
		.overrideTypes<EnrichedProfile[]>();
	return { data, error };
}
